// Which admin-configured external URLs a NATIVE build is allowed to open.
//
// Entry popups and banners let an admin attach an arbitrary `external_url` to the
// CTA. Two of our own hosts resolve to card-checkout pages:
//   hub.stayyoung.live   — the member hub, including the subscribe / renew checkout
//   *payplus.co.il       — PayPlus, the card processor itself
// Opening either from inside the app is exactly the external-purchase path App
// Store rule 3.1.1 forbids, and a reviewer who taps a live banner finds it in
// seconds.
//
// On native we therefore skip the whole popup/banner rather than render a CTA we
// would have to no-op: a dead tap looks broken, a campaign that never appears does
// not. Web keeps every campaign — store rules do not apply in a browser.
//
// Zero dependencies on purpose (mirrors dunningBanner.ts) so the policy stays
// testable in vitest's node environment.

const BLOCKED_EXACT_HOSTS = new Set(["hub.stayyoung.live"]);

// Suffix match, not exact: PayPlus checkout lives on subdomains
// (secure./restapi./…), and the apex is blocked too. Deliberately a plain
// endsWith — over-blocking a lookalike host costs nothing, under-blocking a real
// checkout costs a rejected binary.
const BLOCKED_HOST_SUFFIXES = ["payplus.co.il"];

// SUBSCRIPTION_CHECKOUT_URL is https://www.stayyoung.live/checkouts/fp-147/ — a
// live card checkout on a host we cannot blanket-block, since stayyoung.live also
// serves legitimate marketing pages a banner may well point at. Match the path
// instead: nothing under /checkouts is ever safe to open from a native build.
const BLOCKED_PATH_PREFIXES = ["/checkouts"];

// Campaign URLs are admin-typed and often scheme-less ("hub.stayyoung.live/x").
// openExternalUrl() prepends https:// before opening, so the policy has to
// normalise the same way or a bare host would fail to parse and be blocked by
// accident. Kept in sync with ensureScheme() in @/utils/linkify — not imported,
// because that module pulls in Capacitor and this policy must stay pure.
function withScheme(url: string): string {
  const trimmed = url.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return "https://" + trimmed.replace(/^\/+/, "");
}

/**
 * True when a native build must not open this URL.
 *
 * Malformed, empty, and host-less URLs (javascript:, mailto:, "http://") return
 * true — fail CLOSED. We cannot prove they are safe, and a wrongly-hidden banner
 * is far cheaper than a rejected binary.
 */
export function isBlockedOnNative(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(withScheme(url));
  } catch {
    return true;
  }
  const host = parsed.hostname.toLowerCase();
  if (!host) return true;
  if (BLOCKED_EXACT_HOSTS.has(host)) return true;
  if (BLOCKED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))) return true;

  const path = parsed.pathname.toLowerCase();
  return BLOCKED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** The CTA fields shared by popup and banner campaigns (see PopupCampaign). */
export interface CampaignCtaLike {
  cta_action_type?: string | null;
  cta_value?: string | null;
}

/**
 * True when this campaign's tap action must not be offered on native, and the
 * campaign should therefore be dropped before it is ever picked for display.
 *
 * `isNative` is passed in rather than read from Capacitor so the policy stays a
 * pure function; callers hand it Capacitor.isNativePlatform().
 *
 * In-app navigation CTAs are always fine — they never leave the app.
 */
export function isCampaignBlockedOnNative(
  campaign: CampaignCtaLike,
  isNative: boolean,
): boolean {
  if (!isNative) return false;
  if (campaign.cta_action_type !== "external_url") return false;
  return isBlockedOnNative(campaign.cta_value ?? "");
}

/**
 * How a native build handles this campaign's CTA.
 *   "as_is"   — open it as configured (web, in-app routes, safe external URLs).
 *   "paywall" — a checkout-bound URL while store purchases are live: show the
 *               campaign, but send the tap to the in-app paywall instead. Since
 *               IAP, that is the permitted way to sell on native.
 *   "hidden"  — a checkout-bound URL with store purchases off, or a URL we can't
 *               parse (fail closed; we can't show it was a purchase link).
 */
export type NativeCampaignCta = "as_is" | "paywall" | "hidden";

export function nativeCampaignCta(
  campaign: CampaignCtaLike,
  isNative: boolean,
  iapEnabled: boolean,
): NativeCampaignCta {
  if (!isCampaignBlockedOnNative(campaign, isNative)) return "as_is";
  if (!iapEnabled || !isParseable(campaign.cta_value ?? "")) return "hidden";
  return "paywall";
}

function isParseable(url: string): boolean {
  try {
    return !!new URL(withScheme(url)).hostname;
  } catch {
    return false;
  }
}
